// SPDX-License-Identifier: AGPL-3.0-or-later

import {msg} from '@lingui/core/macro';
import type {SectionDefinition} from './SectionRegistryTypes';

const HOME_UNDO_SEND_DESCRIPTOR = msg({
	message: 'Undo send duration',
	comment: 'Settings section label for the duration of the Home reply undo window.',
});

const PROFILE_CUSTOMIZATION_DESCRIPTOR = msg({
	message: 'Profile customization',
	comment: 'Settings section label for editing profile appearance and identity.',
});
const EMOJI_PACKS_DESCRIPTOR = msg({
	message: 'Emoji packs',
	comment: 'Settings section label for emoji expression packs.',
});
const STICKER_PACKS_DESCRIPTOR = msg({
	message: 'Sticker packs',
	comment: 'Settings section label for sticker expression packs.',
});
const BLOCKED_USERS_DESCRIPTOR = msg({
	message: 'Blocked users',
	comment: 'Settings section label for blocked users.',
});
const AUTHORIZED_APPLICATIONS_DESCRIPTOR = msg({
	message: 'Authorized applications',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const AUTHORIZED_APPS_DESCRIPTOR = msg({
	message: 'Authorized apps',
	comment: 'Settings section label for OAuth applications authorized by the user.',
});
const LINKED_CONNECTIONS_DESCRIPTOR = msg({
	message: 'Connections',
	comment: 'Settings section label for external accounts and verified domains.',
});
const SIGNED_IN_DEVICES_DESCRIPTOR = msg({
	message: 'Signed-in devices',
	comment: 'Settings section label for account sessions and devices.',
});
const TIME_FORMAT_DESCRIPTOR = msg({
	message: 'Time format',
	comment: 'Settings section label for app time display format.',
});
const INTERFACE_LANGUAGE_DESCRIPTOR = msg({
	message: 'Interface language',
	comment: 'Settings section label for interface language selection.',
});
const LANGUAGE_SETTINGS_DESCRIPTOR = msg({
	message: 'Language settings',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const SPELLCHECK_DESCRIPTOR = msg({
	message: 'Spellcheck',
	comment: 'Settings section label for spellcheck settings.',
});
const DESKTOP_WINDOW_DESCRIPTOR = msg({
	message: 'Desktop window',
	comment: 'Settings section label for desktop window behavior.',
});
const DEVELOPER_MODE_DESCRIPTOR = msg({
	message: 'Developer mode',
	comment: 'Settings section label for client developer mode.',
});
const HOME_LAYOUT_DESCRIPTOR = msg({
	message: 'Layout',
	comment: 'Settings section label in Customise Home: Stacks or List.',
});
const HOME_DENSITY_DESCRIPTOR = msg({
	message: 'Density',
	comment: 'Settings section label in Customise Home: comfortable or compact rows.',
});
const HOME_ALL_CHATS_DESCRIPTOR = msg({
	message: 'All chats',
	comment:
		'Settings section label in Customise Home: how the All chats list on Home is grouped (by day or by connection).',
});
const HOME_SECTIONS_DESCRIPTOR = msg({
	message: 'Home sections',
	comment: 'Settings section label in Customise Home: which Home sections show, and their order.',
});
const HOME_ROWS_DESCRIPTOR = msg({
	message: 'Rows before “+N more”',
	comment: 'Settings section label in Customise Home: how many rows a section shows before "+N more".',
});
const HOME_SUGGESTIONS_DESCRIPTOR = msg({
	message: 'Konvo suggestions',
	comment: "Settings section label in Customise Home: the assistant's suggestions as a stack or a list.",
});
const HOME_DECK_DESCRIPTOR = msg({
	message: 'Focus deck',
	comment: 'Settings section label in Customise Home: open Home in the focus deck when there is a lot to do.',
});
const HOME_SWIPES_DESCRIPTOR = msg({
	message: 'Home swipe actions',
	comment: 'Settings section label in Customise Home: what swiping a Home row left or right does.',
});
const HOME_SWIPE_FEEL_DESCRIPTOR = msg({
	message: 'Home swipe feel',
	comment: 'Settings section label in Customise Home: haptics, short-swipe tray and swipe distance.',
});
const MAIL_SIGNATURES_DESCRIPTOR = msg({
	message: 'Signatures',
	comment: 'Settings section for mail signatures (added to the end of mail written in Konvo, importable from Outlook).',
});
const MAIL_REPLYING_DESCRIPTOR = msg({
	message: 'Replying',
	comment: 'Settings section for which reply (Reply all or Reply) the email reader leads with.',
});
const MAIL_SWIPE_ACTIONS_DESCRIPTOR = msg({
	message: 'Swipe actions',
	comment: 'Settings section for choosing the actions performed by swiping a mail row left or right.',
});
const MAIL_SWIPE_TRAY_DESCRIPTOR = msg({
	message: 'Short swipe',
	comment: 'Settings section label for the mail short-swipe tray.',
});
const MAIL_SWIPE_FEEL_DESCRIPTOR = msg({
	message: 'Swipe feel',
	comment: 'Settings section label for mail swipe distance, haptics, undo and delete confirmation.',
});
const MAIL_HIDDEN_FROM_CHATS_DESCRIPTOR = msg({
	message: 'Hidden from Chats',
	comment: 'Settings section label for the rules that keep chosen mail out of the chat lists while it stays in Mail.',
});
const MAIL_TIDY_UP_DESCRIPTOR = msg({
	message: 'FYI mail',
	comment:
		'Settings section label for the inbox card that gathers mail you only need to glance at (notifications, runs, digests).',
});
const UNFURL_DEBUGGER_DESCRIPTOR = msg({
	message: 'Unfurl debugger',
	comment: 'Settings section label for the embed debugger URL input.',
});
const APPLICATIONS_LIST_DESCRIPTOR = msg({
	message: 'Applications',
	comment: 'Settings section label for developer application list.',
});

export const generalSettingsSections = [
	{
		id: 'konvo-home-layout',
		tabType: 'konvo_home',
		label: HOME_LAYOUT_DESCRIPTOR,
		keywords: ['home', 'today', 'layout', 'stacks', 'list', 'customise', 'customize'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-density',
		tabType: 'konvo_home',
		label: HOME_DENSITY_DESCRIPTOR,
		keywords: ['home', 'density', 'compact', 'comfortable', 'rows'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-all-chats',
		tabType: 'konvo_home',
		label: HOME_ALL_CHATS_DESCRIPTOR,
		keywords: [
			'home',
			'all chats',
			'group',
			'grouping',
			'by day',
			'by connection',
			'source',
			'konvo',
			'mail',
			'whatsapp',
		],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-sections',
		tabType: 'konvo_home',
		label: HOME_SECTIONS_DESCRIPTOR,
		keywords: ['home', 'sections', 'order', 'reorder', 'hide', 'show', 'things for today', 'sorted', 'coming up'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-rows',
		tabType: 'konvo_home',
		label: HOME_ROWS_DESCRIPTOR,
		keywords: ['home', 'rows', 'more', 'show more', 'limit'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-suggestions',
		tabType: 'konvo_home',
		label: HOME_SUGGESTIONS_DESCRIPTOR,
		keywords: ['home', 'konvo', 'assistant', 'suggestions', 'stack', 'list'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-deck',
		tabType: 'konvo_home',
		label: HOME_DECK_DESCRIPTOR,
		keywords: ['home', 'focus', 'deck', 'go through', 'one at a time'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-undo-send',
		tabType: 'konvo_home',
		label: HOME_UNDO_SEND_DESCRIPTOR,
		keywords: ['home', 'undo', 'send', 'reply', 'duration', 'delay', 'seconds'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-swipes',
		tabType: 'konvo_home',
		label: HOME_SWIPES_DESCRIPTOR,
		keywords: ['home', 'swipe', 'done', 'reply', 'later', 'dismiss', 'task', 'gesture'],
		isAdvanced: false,
	},
	{
		id: 'konvo-home-swipe-feel',
		tabType: 'konvo_home',
		label: HOME_SWIPE_FEEL_DESCRIPTOR,
		keywords: ['home', 'swipe', 'haptic', 'vibration', 'tray', 'distance'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-replying',
		tabType: 'konvo_mail',
		label: MAIL_REPLYING_DESCRIPTOR,
		keywords: ['mail', 'email', 'reply', 'reply all', 'default', 'main button'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-signatures',
		tabType: 'konvo_mail',
		label: MAIL_SIGNATURES_DESCRIPTOR,
		keywords: ['mail', 'email', 'signature', 'outlook', 'sign-off'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-swipe-actions',
		tabType: 'konvo_mail',
		label: MAIL_SWIPE_ACTIONS_DESCRIPTOR,
		keywords: ['mail', 'email', 'swipe', 'read', 'reply', 'archive', 'delete', 'flag', 'remind', 'task', 'move'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-swipe-tray',
		tabType: 'konvo_mail',
		label: MAIL_SWIPE_TRAY_DESCRIPTOR,
		keywords: ['mail', 'email', 'swipe', 'tray', 'short swipe', 'quick actions'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-swipe-feel',
		tabType: 'konvo_mail',
		label: MAIL_SWIPE_FEEL_DESCRIPTOR,
		keywords: ['mail', 'email', 'swipe', 'distance', 'haptic', 'vibration', 'undo', 'confirm'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-tidy-up',
		tabType: 'konvo_mail',
		label: MAIL_TIDY_UP_DESCRIPTOR,
		keywords: ['mail', 'email', 'fyi', 'tidy', 'clean up', 'notifications', 'github', 'runs', 'digest', 'archive all'],
		isAdvanced: false,
	},
	{
		id: 'konvo-mail-hidden-from-chats',
		tabType: 'konvo_mail',
		label: MAIL_HIDDEN_FROM_CHATS_DESCRIPTOR,
		keywords: ['mail', 'email', 'hide', 'chats', 'filter', 'mute', 'sender', 'subject', 'notifications', 'github'],
		isAdvanced: false,
	},
	{
		id: 'profile-customization',
		tabType: 'my_profile',
		label: PROFILE_CUSTOMIZATION_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
	{id: 'emoji-packs', tabType: 'expression_packs', label: EMOJI_PACKS_DESCRIPTOR, keywords: [], isAdvanced: false},
	{id: 'sticker-packs', tabType: 'expression_packs', label: STICKER_PACKS_DESCRIPTOR, keywords: [], isAdvanced: false},
	{id: 'blocked-users', tabType: 'blocked_users', label: BLOCKED_USERS_DESCRIPTOR, keywords: [], isAdvanced: false},
	{
		id: 'authorized-applications',
		tabType: 'authorized_apps',
		label: AUTHORIZED_APPS_DESCRIPTOR,
		keywords: [AUTHORIZED_APPLICATIONS_DESCRIPTOR],
		isAdvanced: false,
	},
	{
		id: 'connections',
		tabType: 'linked_accounts',
		label: LINKED_CONNECTIONS_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
	{id: 'signed-in-devices', tabType: 'devices', label: SIGNED_IN_DEVICES_DESCRIPTOR, keywords: [], isAdvanced: false},
	{
		id: 'language-settings',
		tabType: 'language',
		label: INTERFACE_LANGUAGE_DESCRIPTOR,
		keywords: [LANGUAGE_SETTINGS_DESCRIPTOR],
		isAdvanced: false,
	},
	{id: 'time-format', tabType: 'language', label: TIME_FORMAT_DESCRIPTOR, keywords: [], isAdvanced: false},
	{id: 'spellcheck', tabType: 'language', label: SPELLCHECK_DESCRIPTOR, keywords: [], isAdvanced: false},
	{
		id: 'desktop-window',
		tabType: 'desktop_settings',
		label: DESKTOP_WINDOW_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
	{
		id: 'developer-mode',
		tabType: 'client_developer_settings',
		label: DEVELOPER_MODE_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
	{
		id: 'unfurl-debugger',
		tabType: 'embed_debugger',
		label: UNFURL_DEBUGGER_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
	{
		id: 'applications-list',
		tabType: 'applications',
		label: APPLICATIONS_LIST_DESCRIPTOR,
		keywords: [],
		isAdvanced: false,
	},
] as const satisfies ReadonlyArray<SectionDefinition>;
